import { ChangeDetectionStrategy, Component, Signal } from '@angular/core';

import {
  DialogCloseReason,
  SuiDialogBase,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogConfig,
} from '@sui/dialog';
import { SuiButtonComponent } from '@sui/button';

export interface I18nKeyRejection {
  key: string;
  message: string;
  route: string;
}

export interface I18nKeyRejectedDialogData {
  rejections: Signal<I18nKeyRejection[]>;
}

// Every string this dialog renders is a plain English literal on purpose: a
// `| translate` pipe here would call MissingTranslationService again and
// register its own keys on the backend (and the i18n-scan would collect them).
export const I18N_KEY_REJECTED_DIALOG_TEXT = {
  title: 'Translation key rejected by the backend',
  intro:
    'The backend refused to register the following translation key(s). ' +
    'Such a key can never load in the app, so this must be fixed now.',
  routeLabel: 'Route',
  fixTitle: 'How to fix',
  fixSteps: [
    'Dot-prefix collision: a key must never be a prefix of another key (no x.title next to x.title.foo). Delete the unreferenced side of the pair from nom_app_language_keyword_v2 and nom_app_language_keyword_v2_i18n, or rename the new key.',
    'Whitespace: the string is user data leaked into the translate pipeline, not a key. Fix the call site so it passes a real key.',
    'Details: .claude/docs/i18n.md (Naming rules) in this repo, ticket SEN-5188.',
  ],
  copyButton: 'Copy details',
  copiedButton: 'Copied',
  dismissButton: 'I will fix it',
} as const;

@Component({
  selector: 'app-i18n-key-rejected-dialog',
  templateUrl: './i18n-key-rejected-dialog.component.html',
  styleUrl: './i18n-key-rejected-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDialogContentSlot,
    SuiDialogFooterSlot,
  ],
})
export class I18nKeyRejectedDialogComponent extends SuiDialogBase<
  I18nKeyRejectedDialogData,
  void
> {
  static dialogConfig: Partial<SuiDialogConfig> = { width: 640 };

  protected readonly text = I18N_KEY_REJECTED_DIALOG_TEXT;

  protected copied = false;

  protected get rejections(): I18nKeyRejection[] {
    return this.dialogData.rejections();
  }

  protected copyDetails(): void {
    const lines = this.rejections.map(
      (rejection) =>
        `${rejection.key}\n  ${rejection.message}\n  route: ${rejection.route}`,
    );
    navigator.clipboard?.writeText(lines.join('\n'));
    this.copied = true;
  }

  protected dismiss(): void {
    this.close({ reason: DialogCloseReason.Confirm });
  }
}
